import React, { useCallback, useState } from 'react'
import {
	Button,
	Checkbox,
	DatePicker,
	Form,
	Input,
	InputNumber,
	message,
	Upload,
} from 'antd'
// eslint-disable-next-line import/no-extraneous-dependencies
import { ValidateErrorEntity } from 'rc-field-form/lib/interface'
import createNewInvoice, { NewInvoiceBody } from 'api/invoice/createNewInvoice'
import handleFormError from 'utils/handleFormError'
import { UploadOutlined } from '@ant-design/icons'
import { AttachmentType } from 'types'
import upload, { FileUpload } from 'api/file/upload'
import { UploadRequestOption } from 'rc-upload/lib/interface'
import getFileNameFromURL from 'utils/getFileNameFromURL'
import handleAxiosError from 'utils/handleAxiosError'
import moment, { Moment } from 'moment'
import { useAppSelector } from 'hooks/reduxHooks'

const { Item } = Form

interface PropTypes {
	clientId: string
	onSuccess: Function
}

interface FormValues
	extends Omit<NewInvoiceBody, 'dueDate' | 'createdDate' | 'isSilentInvoice'> {
	dueDate: Moment
	createdAt: Moment | null
	silentInvoice: boolean
}

const NewInvoiceForm: React.FC<PropTypes> = ({ clientId, onSuccess }) => {
	const [form] = Form.useForm<FormValues>()

	const [loading, setLoading] = useState<boolean>(false)
	const [sampleAttachment, setSampleAttachment] =
		useState<AttachmentType | null>(null)
	const [uploadError, setUploadError] = useState(false)
	const tdsEnabled = useAppSelector(
		state => state.singleClient.clientProfile?.tdsEnabled
	)

	const createdDate = Form.useWatch('createdAt', form)
	const isSilentInvoice = Form.useWatch('silentInvoice', form)

	const uploadFile = async (options: UploadRequestOption<FileUpload>) => {
		try {
			const { url } = await upload(options)
			const { file } = options as any
			setSampleAttachment({
				link: url,
				fileName: file?.name || getFileNameFromURL(url),
			})
		} catch (error: any) {
			handleAxiosError(error)
		}
	}

	const beforeUpload = (file: File) => {
		const isPDF = file.type === 'application/pdf'
		if (!isPDF) {
			message.error(`Only PDF file accepted`)
			setUploadError(true)
		}
		setUploadError(false)
		return isPDF || Upload.LIST_IGNORE
	}

	const handleUploadRemove = useCallback(() => {
		setSampleAttachment(null)
		setTimeout(() => {
			form.setFieldsValue({
				url: undefined,
			})
		}, 0)
	}, [form])

	const onFinish = async (values: FormValues) => {
		if (sampleAttachment === null) {
			message.error('Please upload a PDF file.')
			return
		}
		const body = {
			amount: values.amount,
			client: values.client,
			number: values.number,
			url: sampleAttachment?.link,
			dueDate: values.dueDate.toJSON(),
			deactivation: values.deactivation,
			tdsEnabled: values.tdsEnabled,
			createdDate:
				values.createdAt && values.silentInvoice
					? values.createdAt.toJSON()
					: undefined,
			isSilentInvoice: values.silentInvoice,
		}
		try {
			setLoading(true)
			await createNewInvoice(body)
			setLoading(false)
			message.success('Invoice created.')
			onSuccess(true)
		} catch (error: any) {
			setLoading(false)
			onSuccess(false)
		}
	}

	const onFinishFailed = (errorInfo: ValidateErrorEntity) => {
		handleFormError(errorInfo)
	}

	const handleFieldsChange = () => {
		const { dueDate, createdAt, silentInvoice } = form.getFieldsValue()
		if (dueDate && createdAt) {
			if (dueDate.isBefore(createdAt, 'day')) {
				form.setFieldsValue({ dueDate: undefined })
			}
		}

		if (!silentInvoice) {
			form.setFieldsValue({ createdAt: null })
		}
	}

	return (
		<Form
			form={form}
			name='ceateNewInvoice'
			labelCol={{ span: 20 }}
			wrapperCol={{ span: 20 }}
			onFinish={onFinish}
			onFinishFailed={onFinishFailed}
			onFieldsChange={handleFieldsChange}
			autoComplete='off'
			layout='vertical'
			initialValues={{
				amount: '',
				client: clientId,
				url: '',
				number: '',
				dueDate: null,
				createdAt: null,
				deactivation: false,
				tdsEnabled: tdsEnabled ?? false,
				silentInvoice: false,
			}}
		>
			<Item
				label='Amount'
				name='amount'
				rules={[{ required: true, message: 'Please enter amount!' }]}
			>
				<InputNumber style={{ width: '100%' }} min={1} />
			</Item>
			<Item
				label='Client'
				name='client'
				rules={[{ required: true, message: 'Please enter client id!' }]}
				hidden
			>
				<Input />
			</Item>
			<Item
				label='Upload File'
				name='url'
				rules={[{ required: true, message: 'Please upload a pdf file' }]}
			>
				<Upload
					maxCount={1}
					onRemove={handleUploadRemove}
					beforeUpload={beforeUpload}
					showUploadList={!uploadError}
					multiple={false}
					customRequest={uploadFile}
					defaultFileList={[
						...(sampleAttachment !== null
							? [
									{
										uid: '1',
										name:
											sampleAttachment.fileName ||
											getFileNameFromURL(sampleAttachment.link),
										url: sampleAttachment.link,
									},
							  ]
							: []),
					]}
					accept='APPLICATION/PDF'
				>
					<Button icon={<UploadOutlined />}>Upload a PDF File</Button>
				</Upload>
			</Item>
			<Item
				label='Invoice Number'
				name='number'
				rules={[{ required: true, message: 'Please enter invoice number!' }]}
			>
				<Input />
			</Item>
			<Item
				label='Silent invoice'
				name='silentInvoice'
				valuePropName='checked'
				rules={[{ required: true, message: 'Silent invoice can not be undefined' }]}
			>
				<Checkbox>Do not send Invoice mail.</Checkbox>
			</Item>
			<Item label='Created Date' name='createdAt' hidden={!isSilentInvoice}>
				<DatePicker
					style={{ width: '100%' }}
					disabled={!isSilentInvoice}
					format='DD-MM-YYYY'
					disabledDate={current => {
						return current && !isSilentInvoice && current.isBefore(moment(), 'day')
					}}
				/>
			</Item>
			<Item
				label='Due Date'
				name='dueDate'
				rules={[{ required: true, message: 'Please enter invoice due date!' }]}
			>
				<DatePicker
					style={{ width: '100%' }}
					format='DD-MM-YYYY'
					disabledDate={current => current && current.isBefore(createdDate, 'day')}
				/>
			</Item>

			<Item
				label='Deactivation'
				name='deactivation'
				valuePropName='checked'
				rules={[{ required: true, message: 'Deactivation value is required!' }]}
			>
				<Checkbox>Deactivate the account after due date has passed</Checkbox>
			</Item>

			<Item
				label='TDS'
				name='tdsEnabled'
				valuePropName='checked'
				rules={[{ required: true, message: 'TDS cannot be undefined!' }]}
			>
				<Checkbox>TDS Applicable</Checkbox>
			</Item>

			<Item>
				<Button type='primary' htmlType='submit' loading={loading}>
					Submit
				</Button>
			</Item>
		</Form>
	)
}

export default NewInvoiceForm
